import React, { useMemo, useState } from 'react';
import cx from 'classnames';

import {
    constants,
    getTextDialFilterData,
    locale,
    removeNulls,
} from '@trqk/utils';

import { BroadcastVsDigitalFilter, Dropdown } from '../..';
import {
    ChartInfoTooltip,
    ChartOrEmptyMessage,
    SourceEarningsRanges,
} from '..';
import {
    filterSmallValuesByMaxEarningsPerPlay,
    filterSmallValuesByMaxEarningsPerSecond,
} from '../helpers';

/**
 * @typedef {StatementRangesProps}
 * @property {string} id a unique ID for this data set
 * @property {ProIngestionDashboard} dashboard dashboard data
 * @property {number} containerHeight height of the container element
 * @property {boolean} truncateChartData whether to filter and slice chart data
 */
interface StatementRangesProps {
    /**
     * A unique ID for this data set
     */
    id: string;
    /**
     * Height of the container element
     */
    containerHeight: number;
    /**
     * PRO dashboard data
     */
    dashboard: ProIngestionDashboard;
    /**
     * Whether to filter and slice the chart data
     */
    truncateChartData: boolean;
}

const {
    broadcastVsDigitalOptions,
    broadcastOption,
    broadcastFilterPredicate,
    digitalFilterPredicate,
} = constants;
const payoutRangesPerPerformance =
    'payoutRangesPerPerformance' as StatementRangesChartType;
const payoutRangesPerSecond =
    'payoutRangesPerSecond' as StatementRangesChartType;
const dropdownOptions = [
    {
        label: locale.enUS.STATEMENTS.SECTIONS.EARNINGS_AND_RANGES
            .PAYOUT_RANGES_PER_PERFORMANCE,
        value: payoutRangesPerPerformance,
    },
    {
        label: locale.enUS.STATEMENTS.SECTIONS.EARNINGS_AND_RANGES
            .PAYOUT_RANGES_PER_SECOND,
        value: payoutRangesPerSecond,
    },
];
const defaultChartIndex = 0;
const filterKeys: BroadcastOrDigital[] = ['broadcast', 'digital'];
const maxSize = 50;
const minValue = 0.01;
const chartHeightOffset = 130;

const filterSourceNameNotReported = (source?: Maybe<ProSource>): boolean =>
    source?.source_name !== 'not_reported';

/**
 * Returns a filter predicate function based on either 'broadcast' or 'digital' logic, as defined
 * by packages/utils/src/genreMatchesFilter.ts.
 * The returned predicate also filters out sources with the source_name of 'not_reported'.
 *
 * Pass in "broadcast" to filter down to a list of sources that have:
 * - music_user_genre_name !== 'digital'
 * - source_name !== 'not_reported
 *
 * Pass in "digital" to filter down to a list of sources that have:
 * - music_user_genre_name === 'digital'
 * - source_name !== 'not_reported
 * @param broadcastOrDigital
 */
const getFilterPredicate = (
    broadcastOrDigital: BroadcastOrDigital,
): FilterPredicate<ProSource> => {
    if (broadcastOrDigital === 'broadcast') {
        return (data?: Maybe<ProSource>): boolean =>
            broadcastFilterPredicate(data) && filterSourceNameNotReported(data);
    }

    return (data?: Maybe<ProSource>): boolean =>
        digitalFilterPredicate(data) && filterSourceNameNotReported(data);
};

const broadcastWithoutNotReported = getFilterPredicate('broadcast');
const digitalWithoutNotReported = getFilterPredicate('digital');

/**
 * Displays one of two charts for a given PRODashboard, based on the state of a dropdown.
 * - Channel Payout Ranges ($ per Performance): dumbbell plot of payout ranges per performance
 * - Channel Payout Ranges ($ per Second): dumbbell plot of payout ranges per second
 * @param props {StatementRangesProps}
 */
export const StatementRanges = ({
    dashboard,
    containerHeight,
    id,
    truncateChartData,
}: StatementRangesProps): JSX.Element => {
    const [chartType, setChartType] = useState<StatementRangesChartType>(
        dropdownOptions[defaultChartIndex].value,
    );
    const sources: ProSource[] = removeNulls(dashboard.sources);

    const perPerformanceData = useMemo(
        () =>
            getTextDialFilterData<ProSource>({
                truncateChartData,
                data: [...sources],
                defaultFilterOrder: ['broadcast', 'digital'],
                filterConfigs: {
                    broadcast: {
                        filterPredicate: broadcastWithoutNotReported,
                        maxSize,
                        smallValueFilter: (
                            source?: Maybe<ProSource>,
                        ): boolean =>
                            filterSmallValuesByMaxEarningsPerPlay(
                                source,
                                minValue,
                            ),
                        sort: {
                            key: 'max_earnings_per_play',
                            order: 'desc',
                        },
                    },
                    digital: {
                        filterPredicate: digitalWithoutNotReported,
                        maxSize,
                        smallValueFilter: (
                            source?: Maybe<ProSource>,
                        ): boolean =>
                            filterSmallValuesByMaxEarningsPerPlay(
                                source,
                                minValue,
                            ),
                        sort: {
                            key: 'max_earnings_per_play',
                            order: 'desc',
                        },
                    },
                },
            }),
        [sources],
    );

    const perSecondData = useMemo(
        () =>
            getTextDialFilterData<ProSource>({
                truncateChartData,
                data: [...sources],
                defaultFilterOrder: ['broadcast', 'digital'],
                filterConfigs: {
                    broadcast: {
                        filterPredicate: broadcastWithoutNotReported,
                        maxSize,
                        smallValueFilter: (
                            source?: Maybe<ProSource>,
                        ): boolean =>
                            filterSmallValuesByMaxEarningsPerSecond(
                                source,
                                minValue,
                            ),
                        sort: {
                            key: 'max_earnings_per_second',
                            order: 'desc',
                        },
                    },
                    digital: {
                        filterPredicate: digitalWithoutNotReported,
                        maxSize,
                        smallValueFilter: (
                            source?: Maybe<ProSource>,
                        ): boolean =>
                            filterSmallValuesByMaxEarningsPerSecond(
                                source,
                                minValue,
                            ),
                        sort: {
                            key: 'max_earnings_per_second',
                            order: 'desc',
                        },
                    },
                },
            }),
        [sources],
    );

    const defaultFilterOption =
        broadcastVsDigitalOptions.get(
            (perPerformanceData?.defaultFilter ??
                'broadcast') as BroadcastOrDigital,
        ) ?? broadcastOption;

    const [filterOption, setFilterOption] =
        useState<DropdownOption<BroadcastOrDigital>>(defaultFilterOption);

    const chartHeight = containerHeight - chartHeightOffset;

    return (
        <div style={{ height: containerHeight }}>
            <div className="pt-4 pl-4 mb-6">
                <div className="flex justify-end mr-4">
                    <BroadcastVsDigitalFilter
                        filterOption={filterOption}
                        setFilterOption={setFilterOption}
                    />
                </div>

                <div className="text-sm text-tertiary-400 mb-1">
                    {locale.enUS.SELECT_CHART}
                </div>
                <div className="flex justify-between items-end">
                    <Dropdown
                        defaultIndex={defaultChartIndex}
                        size="medium"
                        options={dropdownOptions}
                        handleChange={(
                            value: StatementRangesChartType,
                        ): void => {
                            setChartType(value);
                        }}
                        width={370}
                    />
                    <div className="mr-4">
                        <ChartInfoTooltip
                            maxSize={maxSize}
                            minValue={minValue}
                        />
                    </div>
                </div>
            </div>
            {/* Channel Payout Ranges ($ per Performance) */}
            <div
                className={cx('mr-2', {
                    hidden: chartType !== payoutRangesPerPerformance,
                })}
            >
                {filterKeys.map(
                    (filterKey: BroadcastOrDigital): JSX.Element => (
                        <div
                            key={filterKey}
                            className={cx({
                                hidden: filterOption.value !== filterKey,
                            })}
                        >
                            <ChartOrEmptyMessage
                                data={
                                    perPerformanceData.filteredData[filterKey]
                                        .data
                                }
                                height={chartHeight}
                            >
                                <SourceEarningsRanges
                                    chartDivId={`${id}.statement-ranges-dollar-per-performance-${filterKey}`}
                                    height={chartHeight}
                                    sources={
                                        perPerformanceData.filteredData[
                                            filterKey
                                        ].data
                                    }
                                    valueAxisLabel={
                                        locale.enUS.STATEMENTS.SECTIONS
                                            .EARNINGS_AND_RANGES
                                            .DOLLARS_PER_PLAY
                                    }
                                    valueYMin="min_earnings_per_play"
                                    valueYMax="max_earnings_per_play"
                                />
                            </ChartOrEmptyMessage>
                        </div>
                    ),
                )}
            </div>
            {/* Channel Payout Ranges ($ per Second) */}
            <div
                className={cx('mr-2', {
                    hidden: chartType !== payoutRangesPerSecond,
                })}
            >
                {filterKeys.map(
                    (filterKey: BroadcastOrDigital): JSX.Element => (
                        <div
                            key={filterKey}
                            className={cx({
                                hidden: filterOption.value !== filterKey,
                            })}
                        >
                            <ChartOrEmptyMessage
                                data={
                                    perSecondData.filteredData[filterKey].data
                                }
                                height={chartHeight}
                            >
                                <SourceEarningsRanges
                                    chartDivId={`${id}.statement-ranges-dollar-per-second-${filterKey}`}
                                    height={chartHeight}
                                    sources={
                                        perSecondData.filteredData[filterKey]
                                            .data
                                    }
                                    valueAxisLabel={
                                        locale.enUS.STATEMENTS.SECTIONS
                                            .EARNINGS_AND_RANGES
                                            .DOLLARS_PER_SECOND
                                    }
                                    valueYMin="min_earnings_per_second"
                                    valueYMax="max_earnings_per_second"
                                />
                            </ChartOrEmptyMessage>
                        </div>
                    ),
                )}
            </div>
        </div>
    );
};
